import {
  type ApiClient,
  type ApiResult,
  type CommandIntent,
  errorMessageFa,
  isApiError,
  UnknownResultError,
} from '@saay/api-client';
import { formatCount, formatTimestamp, toPersianDigits } from '@saay/formatting';
import { type CommandOutcome, useApiClient, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useMemo, useState } from 'react';

import type {
  AdminBuyerSummary,
  PhysicalCardPrintBatch,
  PhysicalCardPrintBatchCreate,
  PhysicalCardPrintBatchCreated,
  PhysicalCardPrintDownload,
  PhysicalCardPrintDownloadRequest,
  PhysicalCardPrintRow,
  PhysicalCardPrintSkipped,
} from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { AdminCommandFeedback, useAdminFeedback } from '../components/admin-feedback.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import { ListLoadMore } from '../components/list-footer.js';
import { ReasonField } from '../components/reason-field.js';
import { SurfaceSection } from '../components/surface-section.js';
import { buyerSearchFromText } from '../buyer-search.js';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  EmptyState,
  ErrorState,
  FieldErrorList,
  FilterChip,
  Masked,
  MultiSelectCombobox,
  SearchField,
  Skeleton,
  applyFieldErrors,
  useWindowClass,
} from '../kit.js';
import {
  listStatus,
  pageRows,
  printKeys,
  useBuyers,
  usePrintBatches,
  usePrintReady,
} from '../queries.js';
import { CardFace, type CardFaceRow } from './physical-card-face.js';
import { savePrintCsv, savePrintXlsx } from './physical-card-print-file.js';
import { printCardSheets } from './physical-card-print-sheet.js';

/**
 * «چاپ کارت» (D-118, AC §32): print batches instead of an issue-date range. The admin sees how many
 * cards are ready, takes the oldest (at most `maxBatchRows`) in one batch with one button, and
 * downloads its file or prints it straight away (D-119, AC §33.9). One buyer's card can be printed
 * apart, and every batch can be taken again from «چاپ‌های قبلی». The print pages and the files (with
 * the CVV2) are built in the browser from the P4 response and are never stored or logged; only counts
 * stay on the page. The preview uses a made-up card.
 */

// ---------------------------------------------------------------------------------------------
// Download (P4): no key, never retried by itself, every call counted (AC §32.8)

/** The admin's lost-response grace (AC §32.8): the same admin within 10 minutes. */
const GRACE_MS = 10 * 60 * 1000;

/** What stays on the page after a download: counts only, never a number. */
export interface DownloadSummary {
  readonly batchNumber: number;
  readonly count: number;
  readonly droppedCount: number;
}

/** What one P4 call feeds: the print dialog or a file (AC §33.9). */
export type PrintOutput = 'csv' | 'print' | 'xlsx';

type DownloadOutcome =
  | { readonly status: 'done'; readonly summary: DownloadSummary }
  | { readonly status: 'reason-required' }
  | { readonly error: unknown; readonly status: 'error' | 'unknown' };

/**
 * Whether a download may go without a reason: the first one, or a repeat while the grace may still
 * apply (the last download was under 10 minutes ago). The server decides; a `422` opens the dialog.
 */
export function mayDownloadWithoutReason(
  batch: Pick<PhysicalCardPrintBatch, 'downloadCount' | 'lastDownloadedAt'>,
  now: number = Date.now(),
): boolean {
  if (batch.downloadCount === 0) return true;
  if (batch.lastDownloadedAt === null) return false;
  const last = Date.parse(batch.lastDownloadedAt);
  return !Number.isNaN(last) && now - last < GRACE_MS;
}

function batchLabel(batchNumber: number): string {
  return `چاپ شمارهٔ ${toPersianDigits(String(batchNumber))}`;
}

function cards(count: number): string {
  return `${formatCount(count)} کارت`;
}

function useBatchDownload() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const feedback = useAdminFeedback();
  return useCallback(
    async (batchId: string, output: PrintOutput, reason?: string): Promise<DownloadOutcome> => {
      const body: PhysicalCardPrintDownloadRequest = reason === undefined ? {} : { reason };
      try {
        const result = await client.postWithoutKey<PhysicalCardPrintDownload>(
          `/admin/physical-card-print-batches/${encodeURIComponent(batchId)}/downloads`,
          { body },
        );
        const { batchNumber, count, droppedCount, items } = result.data;
        const summary = { batchNumber, count, droppedCount };
        if (output === 'csv') savePrintCsv(batchNumber, items);
        if (output === 'xlsx') await savePrintXlsx(batchNumber, items);
        // Every card can drop out of a batch: then there is nothing to print or show.
        if (items.length === 0 && output === 'print') {
          return { status: 'done', summary };
        }
        if (output === 'print') await printCardSheets(items);
        if (output === 'print') {
          feedback.success(`${batchLabel(batchNumber)} برای چاپ باز شد: ${cards(count)}`);
        } else {
          feedback.success(`فایل ${batchLabel(batchNumber)} دریافت شد: ${cards(count)}`);
        }
        return { status: 'done', summary };
      } catch (error) {
        if (
          isApiError(error) &&
          error.code === 'PHYSICAL_CARD_PRINT_REASON_REQUIRED' &&
          error.details?.context === 'REPEAT_DOWNLOAD'
        ) {
          return { status: 'reason-required' };
        }
        return { error, status: error instanceof UnknownResultError ? 'unknown' : 'error' };
      } finally {
        void queryClient.invalidateQueries({ queryKey: printKeys.all });
      }
    },
    [client, feedback, queryClient],
  );
}

/** «۳ کارت … در فایل نیست»: the cards replaced (or of a closed buyer) since the batch was made. */
function DroppedNotice({ summary }: { readonly summary: DownloadSummary }) {
  if (summary.droppedCount === 0) return null;
  return (
    <Banner tone="info">
      {`${cards(summary.droppedCount)} از ${batchLabel(summary.batchNumber)} در فایل نیامد، چون کارتشان عوض شده یا حساب خریدارشان بسته شده. کارت تازه‌شان در نوبت چاپ بعدی است.`}
    </Banner>
  );
}

// ---------------------------------------------------------------------------------------------
// Create (P2) and then download (P4)

const sendCreate = (
  client: ApiClient,
  body: PhysicalCardPrintBatchCreate,
  intent: CommandIntent,
): Promise<ApiResult<PhysicalCardPrintBatchCreated>> =>
  client.post<PhysicalCardPrintBatchCreated>('/admin/physical-card-print-batches', {
    body,
    intent,
  });

/** After P2 succeeded: the file came, or the batch is saved and its file did not come. */
type RunResult =
  | {
      readonly batch: PhysicalCardPrintBatchCreated;
      readonly output: PrintOutput;
      readonly status: 'done';
      readonly summary: DownloadSummary;
    }
  | {
      readonly batch: PhysicalCardPrintBatchCreated;
      readonly error: unknown;
      readonly output: PrintOutput;
      readonly status: 'saved-not-downloaded' | 'saved-unknown';
    };

function usePrintRun(name: string) {
  const queryClient = useQueryClient();
  const download = useBatchDownload();
  const command = useCommand<PhysicalCardPrintBatchCreate, PhysicalCardPrintBatchCreated>(
    name,
    sendCreate,
  );
  const [downloading, setDownloading] = useState(false);
  const [output, setOutput] = useState<PrintOutput>('xlsx');
  const [result, setResult] = useState<RunResult | null>(null);

  async function settle(
    outcome: CommandOutcome<PhysicalCardPrintBatchCreated>,
    wanted: PrintOutput,
  ): Promise<CommandOutcome<PhysicalCardPrintBatchCreated>> {
    if (outcome.status === 'error') {
      void queryClient.invalidateQueries({ queryKey: printKeys.all });
    }
    if (outcome.status !== 'success') return outcome;
    const batch = outcome.result.data;
    setDownloading(true);
    const got = await download(batch.id, wanted);
    setDownloading(false);
    if (got.status === 'done') {
      setResult({ batch, output: wanted, status: 'done', summary: got.summary });
    } else {
      // A first download needs no reason, so `reason-required` cannot happen here; treat it as a miss.
      setResult({
        batch,
        error: got.status === 'reason-required' ? undefined : got.error,
        output: wanted,
        status: got.status === 'unknown' ? 'saved-unknown' : 'saved-not-downloaded',
      });
    }
    return outcome;
  }

  return {
    busy: command.state.status === 'pending' || downloading,
    checkStatus: async () => settle(await command.checkStatus(), output),
    downloading,
    output,
    reset: () => {
      command.reset();
      setResult(null);
    },
    result,
    state: command.state,
    submit: async (body: PhysicalCardPrintBatchCreate, wanted: PrintOutput) => {
      setResult(null);
      setOutput(wanted);
      return settle(await command.submit(body), wanted);
    },
  };
}

/** Whether this output ends in the print dialog, and not in a file. */
function isPrintOutput(output: PrintOutput): boolean {
  return output === 'print';
}

function runMissLine(
  saved: string,
  output: PrintOutput,
  status: 'saved-not-downloaded' | 'saved-unknown',
): string {
  if (isPrintOutput(output)) {
    return status === 'saved-unknown'
      ? `${saved}، ولی معلوم نیست اطلاعات کارت‌ها رسید یا نه. از «چاپ‌های قبلی» چاپش کنید؛ تا ده دقیقه دلیل لازم نیست.`
      : `${saved}، ولی اطلاعات کارت‌ها دریافت نشد. از «چاپ‌های قبلی» چاپش کنید؛ دلیل لازم نیست.`;
  }
  return status === 'saved-unknown'
    ? `${saved}، ولی معلوم نیست فایلش رسید یا نه. اگر فایل را ندارید، از «چاپ‌های قبلی» دوباره دریافتش کنید؛ تا ده دقیقه دلیل لازم نیست.`
    : `${saved}، ولی فایلش دریافت نشد. از «چاپ‌های قبلی» دریافتش کنید؛ دلیل لازم نیست.`;
}

/** The outcome after P2: the dropped line, or why the cards did not come and where to get them. */
function RunNotice({ result }: { readonly result: RunResult | null }) {
  if (result === null) return null;
  if (result.status === 'done') {
    return (
      <>
        <DroppedNotice summary={result.summary} />
        {isPrintOutput(result.output) ? (
          <p className="m-0 text-sm text-muted-foreground">
            اگر چاپ نشد، از «چاپ‌های قبلی» دوباره چاپش کنید؛ تا ده دقیقه دلیل لازم نیست.
          </p>
        ) : null}
      </>
    );
  }
  const saved = `${batchLabel(result.batch.batchNumber)} با ${cards(result.batch.cardCount)} ساخته شد`;
  return (
    <Banner tone="warning">
      <p>{runMissLine(saved, result.output, result.status)}</p>
      {result.status === 'saved-not-downloaded' && result.error !== undefined ? (
        <p>{errorMessageFa(result.error)}</p>
      ) : null}
    </Banner>
  );
}

// ---------------------------------------------------------------------------------------------
// 1. The ready count and the one button

function ReadyPanel({
  apartOpen,
  onPreview,
  onToggleApart,
}: {
  readonly apartOpen: boolean;
  readonly onPreview: () => void;
  readonly onToggleApart: () => void;
}) {
  const offline = useSubmitBlocked();
  const [picked, setPicked] = useState<readonly string[]>([]);
  const all = usePrintReady();
  const options = useMemo(
    () =>
      (all.data?.byCity ?? []).flatMap((row) =>
        row.cityId === null
          ? []
          : [
              {
                label: `${row.cityNameFa ?? ''} (${formatCount(row.readyCount)})`,
                value: row.cityId,
              },
            ],
      ),
    [all.data],
  );
  // A city whose cards all went into a batch leaves the options, and the filter with it.
  const cityIds = picked.filter((cityId) => options.some((option) => option.value === cityId));
  const filtered = usePrintReady(cityIds, cityIds.length > 0);
  const view = cityIds.length > 0 ? filtered : all;
  const legacy = all.data?.byCity.find((row) => row.cityId === null)?.readyCount ?? 0;
  const run = usePrintRun('physical-card-print.create');

  const ready = view.data?.readyCount ?? 0;
  const max = view.data?.maxBatchRows ?? 0;
  const oldest = view.data?.oldestReadyIssuedAt ?? null;
  const nothingAtAll = all.data !== undefined && all.data.readyCount === 0;
  const cannotStart = offline.blocked || ready === 0 || view.data === undefined;
  const start = (output: PrintOutput) =>
    void run.submit(cityIds.length === 0 ? {} : { cityIds }, output);
  const runButton = (output: PrintOutput) => ({
    disabled: cannotStart || (run.busy && run.output !== output),
    loading: run.busy && run.output === output,
    onClick: () => start(output),
  });

  const apartButton = (
    <Button aria-expanded={apartOpen} onClick={onToggleApart} variant="secondary">
      چاپ یک خریدار
    </Button>
  );

  return (
    <SurfaceSection
      description="شماره کامل کارت‌ها و CVV2 فقط در چاپ و فایل می‌آید و در پنل نمی‌ماند."
      id="physical-card-print-ready"
      title="آمادهٔ چاپ"
    >
      <QueryState
        error={all.error}
        onRetry={() => void all.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری کارت‌های آمادهٔ چاپ…" lines={3} />}
        status={all.status}
      >
        {nothingAtAll ? (
          <EmptyState
            action={apartButton}
            description="کارت‌های تازه خودشان اینجا می‌آیند."
            icon="check"
            title="الان کارتی آمادهٔ چاپ نیست."
          />
        ) : (
          <div className="flex flex-col gap-4">
            {view.status === 'error' ? (
              <ErrorState error={view.error} onRetry={() => void view.refetch()} />
            ) : view.data === undefined ? (
              <Skeleton label="در حال شمردن کارت‌ها…" lines={2} />
            ) : (
              <div aria-live="polite" className="flex flex-col gap-1">
                <p className="m-0 flex flex-wrap items-baseline gap-2">
                  <span className="text-3xl font-extrabold tabular-nums">{formatCount(ready)}</span>
                  <span className="text-base font-bold">کارت آمادهٔ چاپ</span>
                </p>
                {oldest === null ? null : (
                  <p className="m-0 text-sm text-muted-foreground">
                    {`قدیمی‌ترین کارت از ${formatTimestamp(oldest, { style: 'long', withTime: false })}`}
                  </p>
                )}
                {ready > max && max > 0 ? (
                  <p className="m-0 text-sm">
                    {`${cards(max)} قدیمی‌تر در این فایل می‌آید و ${cards(ready - max)} برای فایل بعدی می‌ماند.`}
                  </p>
                ) : null}
                {ready === 0 ? (
                  <p className="m-0 text-sm text-muted-foreground">
                    در این شهرها کارتی آمادهٔ چاپ نیست.
                  </p>
                ) : null}
              </div>
            )}
            {options.length === 0 ? null : (
              <MultiSelectCombobox
                {...(legacy > 0
                  ? { hint: 'کارت‌های قدیمی بدون کد شهر فقط بدون فیلتر شهر چاپ می‌شوند.' }
                  : {})}
                label="شهر (اختیاری)"
                onChange={(next) => {
                  run.reset();
                  setPicked(next);
                }}
                options={options}
                placeholder="همهٔ شهرها"
                value={cityIds}
              />
            )}
            <AdminCommandFeedback
              onCheckStatus={() => void run.checkStatus()}
              pendingLabel="در حال ساخت چاپ…"
              state={run.state}
            />
            {run.downloading ? (
              <p aria-live="polite" className="m-0" role="status">
                {isPrintOutput(run.output) ? 'در حال آماده‌سازی چاپ…' : 'در حال آماده‌سازی فایل…'}
              </p>
            ) : null}
            <RunNotice result={run.result} />
            {offline.reason === undefined ? null : <Banner tone="warning">{offline.reason}</Banner>}
            <div className="flex flex-wrap items-center gap-2">
              <Button className="rounded-full px-5" icon="printer" {...runButton('print')}>
                چاپ کارت‌ها
              </Button>
              <Button
                className="rounded-full px-5"
                icon="download"
                variant="secondary"
                {...runButton('xlsx')}
              >
                دریافت فایل چاپ
              </Button>
              <Button icon="eye" onClick={onPreview} variant="secondary">
                پیش‌نمایش چاپ
              </Button>
              {apartButton}
              <Button aria-label="دریافت فایل CSV" size="sm" variant="ghost" {...runButton('csv')}>
                CSV
              </Button>
            </div>
            <p className="admin-muted">
              در پنجرهٔ چاپ: پرینتر Evolis Pebble 4، جهت Landscape، مقیاس ۱۰۰٪ و بدون حاشیه را
              انتخاب کنید.
            </p>
          </div>
        )}
      </QueryState>
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// 2. One buyer's card apart (`buyerIds`)

const MAX_BUYERS = 500;

const SKIP_FA: Readonly<Record<string, string>> = {
  BUYER_CLOSED: 'حسابش بسته شده',
  CARD_BLOCKED: 'کارتش مسدود است',
  NO_CARD: 'هنوز کارت ندارد',
};

/** Why a picked buyer was left out, in plain words; an unknown reason reads neutral. */
export function skipReasonFa(reason: string): string {
  return SKIP_FA[reason] ?? 'چاپ نشد';
}

function SkippedList({
  names,
  skipped,
}: {
  readonly names: ReadonlyMap<string, string>;
  readonly skipped: readonly PhysicalCardPrintSkipped[];
}) {
  if (skipped.length === 0) return null;
  return (
    <Banner tone="info">
      <p>این خریداران در فایل نیستند:</p>
      <ul className="m-0 list-disc ps-5">
        {skipped.map((row) => (
          <li
            key={row.buyerId}
          >{`${names.get(row.buyerId) ?? 'خریدار'}: ${skipReasonFa(row.reason)}`}</li>
        ))}
      </ul>
    </Banner>
  );
}

function BuyerPicker({
  onChange,
  selected,
}: {
  readonly onChange: (buyers: readonly AdminBuyerSummary[]) => void;
  readonly selected: readonly AdminBuyerSummary[];
}) {
  const [query, setQuery] = useState('');
  const parsed = useMemo(() => buyerSearchFromText(query), [query]);
  const filters = 'filters' in parsed ? parsed.filters : {};
  const searchMessage = 'message' in parsed ? parsed.message : undefined;
  const buyers = useBuyers(filters, query.trim() !== '' && searchMessage === undefined);
  const rows = pageRows(buyers.data);
  const selectedIds = new Set(selected.map((row) => row.buyerId));

  function toggle(row: AdminBuyerSummary): void {
    if (selectedIds.has(row.buyerId)) {
      onChange(selected.filter((item) => item.buyerId !== row.buyerId));
    } else if (selected.length < MAX_BUYERS) {
      onChange([...selected, row]);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {selected.length === 0 ? null : (
        <div className="admin-inline admin-chip-row">
          {selected.map((row) => (
            <FilterChip
              key={row.buyerId}
              onRemove={() => onChange(selected.filter((item) => item.buyerId !== row.buyerId))}
            >
              {`${row.firstNameFa} ${row.lastNameFa}`}
            </FilterChip>
          ))}
        </div>
      )}
      <SearchField
        label="جست‌وجوی خریدار"
        onChange={(event) => setQuery(event.target.value)}
        placeholder="نام، موبایل یا کد ملی"
        value={query}
      />
      {searchMessage === undefined ? null : (
        <p className="admin-muted" role="status">
          {searchMessage}
        </p>
      )}
      {query.trim() === '' ? null : (
        <QueryState
          empty={<EmptyState title="خریداری با این جست‌وجو پیدا نشد" />}
          error={buyers.error}
          isEmpty={rows.length === 0}
          onRetry={() => void buyers.refetch()}
          skeleton={<Skeleton label="در حال جست‌وجو…" lines={3} />}
          status={buyers.status}
        >
          <ul className="admin-plain-list">
            {rows.slice(0, 25).map((row) => (
              <li key={row.buyerId}>
                <Checkbox
                  checked={selectedIds.has(row.buyerId)}
                  label={
                    <span className="admin-inline">
                      <strong>{`${row.firstNameFa} ${row.lastNameFa}`}</strong>
                      <Masked kind="mobile" value={row.mobileMasked} />
                    </span>
                  }
                  onChange={() => toggle(row)}
                />
              </li>
            ))}
          </ul>
        </QueryState>
      )}
    </div>
  );
}

const REASON_FA = 'دلیل را بنویسید.';

function PrintApart() {
  const offline = useSubmitBlocked();
  const [picked, setPicked] = useState<readonly AdminBuyerSummary[]>([]);
  /** The names of the buyers sent, for the skipped list after the picks are cleared. */
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [reprint, setReprint] = useState<{ readonly alreadyPrinted: number } | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const [skipped, setSkipped] = useState<readonly PhysicalCardPrintSkipped[]>([]);
  const run = usePrintRun('physical-card-print.apart');

  function pick(next: readonly AdminBuyerSummary[]): void {
    setPicked(next);
    setReprint(null);
    setReason('');
    setReasonError(undefined);
    run.reset();
  }

  async function send(output: PrintOutput): Promise<void> {
    if (picked.length === 0) return;
    const trimmed = reason.trim();
    if (reprint !== null && trimmed === '') {
      setReasonError(REASON_FA);
      return;
    }
    setReasonError(undefined);
    setUnmapped([]);
    setSkipped([]);
    setNames(new Map(picked.map((row) => [row.buyerId, `${row.firstNameFa} ${row.lastNameFa}`])));
    const outcome = await run.submit(
      {
        buyerIds: picked.map((row) => row.buyerId),
        ...(reprint === null ? {} : { reason: trimmed }),
      },
      output,
    );
    if (outcome.status === 'success') {
      setSkipped(outcome.result.data.skipped);
      setPicked([]);
      setReprint(null);
      setReason('');
      return;
    }
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const { error } = outcome;
    if (
      error.code === 'PHYSICAL_CARD_PRINT_REASON_REQUIRED' &&
      error.details?.context === 'REPRINT'
    ) {
      const count = error.details.alreadyPrintedCount;
      setReprint({ alreadyPrinted: typeof count === 'number' ? count : picked.length });
      return;
    }
    if (
      error.code === 'PHYSICAL_CARD_PRINT_NOTHING_READY' &&
      Array.isArray(error.details?.skipped)
    ) {
      setSkipped(error.details.skipped as PhysicalCardPrintSkipped[]);
    }
    setUnmapped(
      applyFieldErrors(error, ['reason'], (_name, value) => setReasonError(value.message)),
    );
  }

  const asking = reprint !== null;
  // The reason prompt replaces the server's refusal; any other answer shows as usual.
  const askedState =
    asking && run.state.status === 'error' ? { ...run.state, status: 'idle' as const } : run.state;

  return (
    <SurfaceSection
      description="کارت خریداری را که لازم است جدا چاپ کنید. چاپ دوبارهٔ کارتی که قبلاً چاپ شده دلیل می‌خواهد."
      id="physical-card-print-apart"
      title="چاپ یک خریدار"
    >
      <BuyerPicker onChange={pick} selected={picked} />
      {asking ? (
        <div className="flex flex-col gap-2">
          <Banner tone="warning">
            {reprint.alreadyPrinted > 1
              ? `کارت ${formatCount(reprint.alreadyPrinted)} نفر از این خریداران قبلاً چاپ شده. برای چاپ دوباره دلیلش را بنویسید.`
              : 'کارت این خریدار قبلاً چاپ شده. برای چاپ دوباره دلیلش را بنویسید.'}
          </Banner>
          <ReasonField
            error={reasonError}
            intent="physical-card-print.reprint"
            onChange={(value) => {
              setReason(value);
              if (value.trim() !== '') setReasonError(undefined);
            }}
            value={reason}
          />
        </div>
      ) : null}
      <FieldErrorList errors={unmapped} />
      <AdminCommandFeedback
        onCheckStatus={() => void run.checkStatus()}
        pendingLabel="در حال ساخت چاپ…"
        state={askedState}
      />
      {run.downloading ? (
        <p aria-live="polite" className="m-0" role="status">
          {isPrintOutput(run.output) ? 'در حال آماده‌سازی چاپ…' : 'در حال آماده‌سازی فایل…'}
        </p>
      ) : null}
      <RunNotice result={run.result} />
      <SkippedList names={names} skipped={skipped} />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          className="rounded-full px-5"
          disabled={offline.blocked || picked.length === 0 || (run.busy && run.output !== 'print')}
          icon="printer"
          loading={run.busy && run.output === 'print'}
          onClick={() => void send('print')}
        >
          {asking ? 'چاپ دوباره' : 'چاپ کارت این خریدار'}
        </Button>
        <Button
          className="rounded-full px-5"
          disabled={offline.blocked || picked.length === 0 || (run.busy && run.output !== 'xlsx')}
          icon="download"
          loading={run.busy && run.output === 'xlsx'}
          onClick={() => void send('xlsx')}
          variant="secondary"
        >
          {asking ? 'چاپ دوباره و دریافت فایل' : 'دریافت فایل این خریدار'}
        </Button>
      </div>
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// 3. «چاپ‌های قبلی»

const KIND_FA: Readonly<Record<string, string>> = {
  AUTOMATIC: 'خودکار',
  REPRINT: 'چاپ دوباره',
};

export function batchKindFa(kind: string): string {
  return KIND_FA[kind] ?? 'نامشخص';
}

function downloadsFa(count: number): string {
  return count === 0 ? 'هنوز دریافت نشده' : `${formatCount(count)} بار`;
}

function actionLabel(batch: PhysicalCardPrintBatch): string {
  return batch.downloadCount === 0 ? 'دریافت فایل' : 'دریافت دوباره';
}

/** The required reason of a repeat download (AC §32.8): full numbers and CVV2 leave again. */
function RepeatDownloadDialog({
  batch,
  onClose,
  onDone,
  output,
}: {
  readonly batch: PhysicalCardPrintBatch;
  readonly onClose: () => void;
  readonly onDone: (summary: DownloadSummary) => void;
  readonly output: PrintOutput;
}) {
  const download = useBatchDownload();
  const offline = useSubmitBlocked();
  const formId = useId();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ error: unknown; unknown: boolean } | null>(null);
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const printing = isPrintOutput(output);

  async function submit(): Promise<void> {
    const trimmed = reason.trim();
    if (trimmed === '') {
      setReasonError(REASON_FA);
      return;
    }
    if (pending || offline.blocked) return;
    setPending(true);
    setFailure(null);
    setUnmapped([]);
    const got = await download(batch.id, output, trimmed);
    setPending(false);
    if (got.status === 'done') {
      onDone(got.summary);
      onClose();
      return;
    }
    if (got.status === 'reason-required') return;
    if (got.status === 'error' && isApiError(got.error)) {
      setUnmapped(
        applyFieldErrors(got.error, ['reason'], (_name, value) => setReasonError(value.message)),
      );
    }
    setFailure({ error: got.error, unknown: got.status === 'unknown' });
  }

  return (
    <Dialog
      description="شماره کامل کارت‌ها و CVV2 دوباره از سیستم بیرون می‌رود، برای همین دلیلش ثبت می‌شود."
      footer={
        <DialogActions
          primary={
            <Button disabled={offline.blocked} form={formId} loading={pending} type="submit">
              {printing ? 'چاپ کارت‌ها' : 'دریافت فایل'}
            </Button>
          }
          secondary={
            <Button onClick={onClose} variant="secondary">
              انصراف
            </Button>
          }
        />
      }
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open
      size="small"
      title={
        printing
          ? `چاپ دوبارهٔ کارت‌های ${batchLabel(batch.batchNumber)}`
          : `دریافت دوبارهٔ ${batchLabel(batch.batchNumber)}`
      }
    >
      <form
        className="flex flex-col gap-3"
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <ReasonField
          error={reasonError}
          intent="physical-card-print.download"
          onChange={(value) => {
            setReason(value);
            if (value.trim() !== '') setReasonError(undefined);
          }}
          value={reason}
        />
        <FieldErrorList errors={unmapped} />
        {failure === null ? null : failure.unknown ? (
          <Banner tone="warning">
            معلوم نیست فایل رسید یا نه. اگر فایل دانلود نشد، دوباره دریافتش کنید.
          </Banner>
        ) : (
          <ErrorState error={failure.error} title="فایل دریافت نشد" />
        )}
        {offline.reason === undefined ? null : <Banner tone="warning">{offline.reason}</Banner>}
      </form>
    </Dialog>
  );
}

function PrintHistory() {
  const offline = useSubmitBlocked();
  const compact = useWindowClass('expanded') === 'compact';
  const download = useBatchDownload();
  const batches = usePrintBatches();
  const rows = pageRows(batches.data);
  const [busy, setBusy] = useState<{ readonly id: string; readonly output: PrintOutput } | null>(
    null,
  );
  const [asking, setAsking] = useState<{
    readonly batch: PhysicalCardPrintBatch;
    readonly output: PrintOutput;
  } | null>(null);
  const [last, setLast] = useState<DownloadSummary | null>(null);
  const [failure, setFailure] = useState<{
    readonly batchNumber: number;
    readonly error: unknown;
    readonly unknown: boolean;
  } | null>(null);

  async function start(batch: PhysicalCardPrintBatch, output: PrintOutput): Promise<void> {
    setFailure(null);
    setLast(null);
    // Every P4 call counts against the hourly limit, so a call that surely needs a reason asks first.
    if (!mayDownloadWithoutReason(batch)) {
      setAsking({ batch, output });
      return;
    }
    setBusy({ id: batch.id, output });
    const got = await download(batch.id, output);
    setBusy(null);
    if (got.status === 'done') setLast(got.summary);
    else if (got.status === 'reason-required') setAsking({ batch, output });
    else {
      setFailure({
        batchNumber: batch.batchNumber,
        error: got.error,
        unknown: got.status === 'unknown',
      });
    }
  }

  function actionButtons(batch: PhysicalCardPrintBatch) {
    const label = batchLabel(batch.batchNumber);
    const button = (output: PrintOutput) => ({
      disabled:
        offline.blocked || (busy !== null && (busy.id !== batch.id || busy.output !== output)),
      loading: busy?.id === batch.id && busy.output === output,
      onClick: () => void start(batch, output),
    });
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          aria-label={`چاپ کارت‌های ${label}`}
          icon="printer"
          size="sm"
          variant="secondary"
          {...button('print')}
        >
          چاپ
        </Button>
        <Button
          aria-label={`${actionLabel(batch)} ${label}`}
          icon="download"
          size="sm"
          variant="secondary"
          {...button('xlsx')}
        >
          {actionLabel(batch)}
        </Button>
        <Button aria-label={`فایل CSV ${label}`} size="sm" variant="ghost" {...button('csv')}>
          CSV
        </Button>
      </div>
    );
  }

  const columns: readonly DataTableColumn<PhysicalCardPrintBatch>[] = [
    {
      header: 'شماره چاپ',
      id: 'batchNumber',
      numeric: true,
      render: (row) => toPersianDigits(String(row.batchNumber)),
    },
    {
      header: 'تاریخ',
      id: 'createdAt',
      render: (row) => <time dateTime={row.createdAt}>{formatTimestamp(row.createdAt)}</time>,
    },
    {
      header: 'تعداد کارت',
      id: 'cardCount',
      numeric: true,
      render: (row) => formatCount(row.cardCount),
    },
    { header: 'نوع', id: 'kind', render: (row) => batchKindFa(row.kind) },
    { header: 'چه کسی', id: 'createdBy', render: (row) => row.createdBy.displayNameFa },
    {
      header: 'دفعات دریافت',
      id: 'downloadCount',
      render: (row) => downloadsFa(row.downloadCount),
    },
    {
      header: <span className="sr-only">دریافت</span>,
      id: 'action',
      render: (row) => actionButtons(row),
    },
  ];

  return (
    <SurfaceSection id="physical-card-print-history" title="چاپ‌های قبلی">
      {last === null ? null : <DroppedNotice summary={last} />}
      {failure === null ? null : failure.unknown ? (
        <Banner tone="warning">
          {`معلوم نیست فایل ${batchLabel(failure.batchNumber)} رسید یا نه. اگر فایل را ندارید، دوباره دریافتش کنید؛ تا ده دقیقه بعد از دریافت اول دلیل لازم نیست.`}
        </Banner>
      ) : (
        <ErrorState error={failure.error} title="فایل دریافت نشد" />
      )}
      <QueryState
        empty={<EmptyState title="هنوز چاپی ساخته نشده." />}
        error={batches.error}
        isEmpty={rows.length === 0}
        onRetry={() => void batches.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری چاپ‌های قبلی…" lines={4} />}
        status={listStatus(batches)}
      >
        {compact ? (
          <ul aria-label="چاپ‌های قبلی" className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => (
              <li className="flex flex-col gap-2 rounded-2xl bg-background p-3" key={row.id}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-bold">{batchLabel(row.batchNumber)}</span>
                  <span className="text-xs text-muted-foreground">{batchKindFa(row.kind)}</span>
                </div>
                <span className="text-sm">
                  {`${cards(row.cardCount)}، ${formatTimestamp(row.createdAt)}`}
                </span>
                <span className="text-xs text-muted-foreground">
                  {`${row.createdBy.displayNameFa}، دریافت: ${downloadsFa(row.downloadCount)}`}
                </span>
                {actionButtons(row)}
              </li>
            ))}
          </ul>
        ) : (
          <AdminListTable
            caption="چاپ‌های قبلی"
            columns={columns}
            regionLabel="جدول چاپ‌های قبلی"
            mobileSummary={(row) => ({
              primary: batchLabel(row.batchNumber),
              secondary: cards(row.cardCount),
            })}
            rowKey={(row) => row.id}
            rows={rows}
          />
        )}
        <ListLoadMore
          hasMore={batches.hasNextPage}
          loading={batches.isFetchingNextPage}
          onLoadMore={() => void batches.fetchNextPage()}
        />
        {batches.isFetchNextPageError ? (
          <ErrorState error={batches.error} onRetry={() => void batches.fetchNextPage()} />
        ) : null}
      </QueryState>
      {asking === null ? null : (
        <RepeatDownloadDialog
          batch={asking.batch}
          onClose={() => setAsking(null)}
          onDone={(summary) => setLast(summary)}
          output={asking.output}
        />
      )}
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// 4. «پیش‌نمایش چاپ»

/** A made-up card: the preview shows the layout only, so it creates no batch and reads no card. */
const PREVIEW_SAMPLE: CardFaceRow = {
  cardNumberGrouped: '0000 0000 0000 0000',
  citySerial: '000000',
  cvv2: '000',
  expiryFull: '1410/08',
  holderNameFa: 'نام دارنده',
  qrText: 'https://saay.example/c/preview',
};

/**
 * The card layout in gold on the dark card, as it will look (the page that goes to the printer is
 * black on white). It uses a made-up card: no batch is created, and no card leaves «آمادهٔ چاپ».
 */
function PrintPreviewDialog({ onClose }: { readonly onClose: () => void }) {
  return (
    <Dialog
      description="نمونهٔ طرح با اطلاعات ساختگی است. با این کار چاپی ثبت نمی‌شود."
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            بستن
          </Button>
        </div>
      }
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open
      title="پیش‌نمایش چاپ"
    >
      <div className="flex justify-center">
        <CardFace mode="screen" row={PREVIEW_SAMPLE} />
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------

/** The «چاپ کارت» tab of «کارت‌های فیزیکی». */
export function PhysicalCardPrintPanel() {
  const [apartOpen, setApartOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <ReadyPanel
        apartOpen={apartOpen}
        onPreview={() => setPreviewOpen(true)}
        onToggleApart={() => setApartOpen((open) => !open)}
      />
      {apartOpen ? <PrintApart /> : null}
      <PrintHistory />
      {previewOpen ? <PrintPreviewDialog onClose={() => setPreviewOpen(false)} /> : null}
    </div>
  );
}
